import React from 'react';
import style from './style.module.css';
import { Button, IconButton, Tooltip, Typography, CircularProgress } from '@material-ui/core';
import { ButtonColors, IActionButton, IButtonProps, IconPosition } from './types';
import colors from 'design-v2/globals/colors/colors.module.css';
import classNames from 'classnames';
import IconsService from 'external-services/icons-service/icons.service';

const PRIMARY = 'primary';
const SECONDARY = 'secondary';
const SMALL = 'small';
const REGULAR = 'regular';
const LARGE = 'large';
// for icon size
export default function ActionButton(props: IActionButton) {
  const handleClick = () => {
    if (!props.useAnchorHref) {
      props.handleClick();
    }
  };
  const buttonProps: IButtonProps = {
    onClick: handleClick,
    disabled: props.disabled,
    style: {
      maxWidth: props.maxWidth ?? 200,
    },
  } as IButtonProps;
  if (props.variant === PRIMARY) {
    buttonProps.variant = 'contained';
  } else if (props.variant === SECONDARY) {
    buttonProps.variant = 'outlined';
  }
  // Conditional props ------ for Icon
  let Icon;
  if (props.icon) {
    Icon = IconsService.getIcon(props.icon.iconType);
    // check text
    if (props.text) {
      // check position
      if (props.icon.iconPosition === IconPosition.LEFT) {
        buttonProps.startIcon = <Icon />;
      } else if (props.icon.iconPosition === IconPosition.RIGHT) {
        buttonProps.endIcon = <Icon />;
      }
    }
  }
  // Conditional props ------ for Anchor href
  if (props.useAnchorHref) {
    buttonProps.href = '';
    buttonProps.target = '_blank';
  }

  const textClasses = {
    [style.fontThemeColor]: props.useThemeColor,
    [style.singleLineTruncation]: props.useSingleLineTruncation,
  };
  const classes = {
    [style.root]: true,
    [style.rootFullWidth]: props.fullWidth,
    [style.rootPrimary]: props.variant === PRIMARY,
    [style.rootSecondary]: props.variant === SECONDARY,
    [colors.bgClrNbiMagenta]: props.variant === PRIMARY,
    [style.smallSize]: props.size === SMALL,
    [style.regularSize]: props.size === REGULAR,
    [style.largeSize]: props.size === LARGE,
    [style.warningOverride]: props.colorOverride === ButtonColors.WARNING,
    [style.errorOverride]: props.colorOverride === ButtonColors.ERROR,
    [style.infoOverride]: props.colorOverride === ButtonColors.INFO,
    [style.standbyOverride]: props.colorOverride === ButtonColors.STANDBYBLUE,
    [style.successOverride]: props.colorOverride === ButtonColors.SUCCESS,
    [style.rootInButtonGroup]: props.inButtonGroup,
    [style.borderRadiusV2]: props.useBorderRadiusV2,
    [style.borderThemeColor]: props.useThemeColor,
    [style.borderGreyColor]: props.useGreyBolderColor,
    [style.fontBlackColor]: props.useBlackFontColor,
    [style.hideButton]: props.isHide,
  };

  // 1 for only Icon without text
  if (props.icon && !props.text) {
    if (props.tooltip) {
      return (
        <React.Fragment>
          <Tooltip
            className={classNames(classes)}
            title={
              <Typography
                className={classNames({
                  [style.smallSize]: props.size === SMALL,
                  [style.regularSize]: props.size === REGULAR,
                  [style.largeSize]: props.size === LARGE,
                })}
              >
                {props.tooltip.title}
              </Typography>
            }
            placement={props.tooltip.position}
          >
            <IconButton
              ref={props.buttonRef}
              className={classNames(classes)}
              aria-label={props.icon.iconAriaLabel}
              disabled={props.disabled}
            >
              <Icon
                className={classNames({
                  [style.smallSize]: props.size === SMALL,
                  [style.regularSize]: props.size === REGULAR,
                  [style.largeSize]: props.size === LARGE,
                  [style.fontThemeColor]: props.useThemeColor,
                })}
              />
            </IconButton>
          </Tooltip>
        </React.Fragment>
      );
    } else {
      return (
        <IconButton
          className={classNames(classes)}
          aria-label={props.icon.iconAriaLabel}
          disabled={props.disabled}
          ref={props.buttonRef}
        >
          <Icon
            className={classNames({
              [style.smallSize]: props.size === SMALL,
              [style.regularSize]: props.size === REGULAR,
              [style.largeSize]: props.size === LARGE,
              [style.fontThemeColor]: props.useThemeColor,
            })}
          />
        </IconButton>
      );
    }
    // 2 for icon with text
  } else {
    // 2.1 for has tooltip
    if (props.tooltip) {
      return (
        <React.Fragment>
          <Tooltip
            className={classNames(classes)}
            title={
              <Typography
                className={classNames({
                  [style.smallSize]: props.size === SMALL,
                  [style.regularSize]: props.size === REGULAR,
                  [style.largeSize]: props.size === LARGE,
                  [style.fontThemeColor]: props.useThemeColor,
                })}
              >
                {props.tooltip.title}
              </Typography>
            }
            placement={props.tooltip.position}
          >
            <Button
              ref={props.buttonRef}
              disableRipple
              className={classNames(classes)}
              {...buttonProps}
              disabled={props.disabled}
              data-testid={`ActionButton-${props.text}`}
            >
              <span className={classNames(textClasses)}>{props.text}</span>
            </Button>
          </Tooltip>
        </React.Fragment>
      );
      // 2.2 for no tooltip
    } else {
      return (
        <Button
          disableRipple
          className={classNames({
            ...classes,
            [style.iconSmall]: props.size === SMALL,
            [style.iconRegular]: props.size === REGULAR,
            [style.iconLarge]: props.size === LARGE,
            [style.fontThemeColor]: props.useThemeColor,
            [style.hideButton]: props.isHide,
          })}
          {...buttonProps}
          disabled={props.disabled || props.loading}
          data-testid={`ActionButton-${props.text}`}
          ref={props.buttonRef}
        >
          {props.loading && (
            <div className={style.loadingWrapper}>
              <CircularProgress size={20} />
            </div>
          )}
          <span className={classNames(textClasses)}>{props.text}</span>
        </Button>
      );
    }
  }
}
